import { inject as service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { tracked } from '@glimmer/tracking';
import { A } from '@ember/array';
import { action, computed } from '@ember/object';
import { next } from '@ember/runloop';
import { isPresent } from '@ember/utils';
import { dropTask } from 'ember-concurrency';

import { getStickersDeferred } from 'mewe/utils/emoji-utils';
import { reposition } from 'mewe/utils/popup-utils';
import PurchasesStore from 'mewe/stores/purchases-store';
import Scrolling from 'mewe/utils/scrolling-utils';
import dispatcher from 'mewe/dispatcher';
import StickerApi from 'mewe/api/sticker-api';
import { isEmpty } from '@ember/utils';
import { fetchStoreItems } from 'mewe/fetchers/fetch-store';

import PopupOpener from 'mewe/pods/components/basic-ui/mw-popup/base/component';
import layout from './template.hbs';
import './styles.scss';

export default class MwStickersPicker extends PopupOpener {
  layout = layout;

  scrolling = Scrolling();
  popupType = 'stickers-picker';
  dropdownId = 'stickers-picker';

  closeOnScroll = false;
  closeOnHoverOutside = false;
  closeOnClickOutside = true;

  @service account;
  @service dynamicPopups;
  @service features;

  @tracked element;
  @tracked showPopup = true;
  @tracked stickers = null;
  @tracked selectedId;
  @tracked aiStickers = A([]);
  @tracked scrollIndex = 0;
  @tracked visiblePacksCount = 8;
  @tracked showStickerPicker = true;
  @tracked storePacksChecked = false;
  @tracked
  packs = A([
    { id: 'summer-fun_free', name: 'Summer Fun', isFree: true },
    { id: 'holiday23_free', name: 'Holiday 2023', isFree: true },
    { id: 'coffee-break', name: 'Coffee Break' },
    { id: 'mewe-vibes', name: 'Mewe Vibes' },
    { id: 'christmas-merry', name: 'Christmas Merry' },
    { id: 'naughty-dozen', name: 'Naughty Dozen' },
    { id: 'spring-fling', name: 'Spring Fling' },
    { id: 'winter-joy', name: 'Winter Joy' },
    { id: 'donuts-jelly', name: 'Donuts Jelly' },
    { id: 'smiley-guy', name: 'Smiley Guy' },
    { id: 'happy-poo', name: 'Happy Poo' },
    { id: 'avocado-adventures', name: 'Avocado Adventures' },
    { id: 'peach-life', name: 'Peach Life' },
    { id: 'unicorn-life', name: 'Unicorn Life' },
    { id: 'woman-power', name: 'Woman Power' },
    { id: 'eggplant-life', name: 'Eggplant Life' },
    { id: 'mermaid-life', name: 'Mermaid Life' },
    { id: 'sweet-sassy', name: 'Sweet Sassy' },
    { id: 'halloween-party', name: 'Halloween Party' },
  ]);

  constructor() {
    super(...arguments);

    getStickersDeferred().promise.then((data) => {
      this.stickers = data;
    });

    this.storeState = PurchasesStore.getState();

    if (this.account.activeUser.ambassador) {
      // Find the position after the last free pack
      const lastFreePackIndex = this.packs.findLastIndex((pack) => pack.isFree);
      const insertIndex = lastFreePackIndex + 1;
      this.packs.splice(insertIndex, 0, { id: 'ambassador-pack_limited', name: 'Ambassador Pack', isFree: true });
    }

    if (this.showMyAndAiStickers) {
      // these items will be `hidden: true` because they are not returned by the API
      // and this is desired because these items are handled separately in template
      this.packs.unshift({ id: 'my_sticker', name: 'My Stickers' });
      this.packs.unshift({ id: 'gen_sticker', name: 'AI Stickers' });
    }

    next(() => {
      this.mapStoreItems();

      const lastPurchasedItem = this.storeState.lastPurchasedItem;

      if (lastPurchasedItem?.startsWith('stickers-')) {
        this.selectedId = lastPurchasedItem.replace('stickers-', '');
        this.calculateScrollIndex();
      } else if (this.storeState.lastUsedStickerPack) {
        this.selectedId = this.storeState.lastUsedStickerPack;
        this.calculateScrollIndex();
      }
      if (!this.selectedId) {
        this.selectedId = this.packs.find((p) => p.isFree)?.id;
      }
    });
  }

  calculateScrollIndex() {
    const isRtl = document.dir;
    let newIndex;
    const selectedIndex = this.packs.findIndex((pack) => pack.id === this.selectedId);

    if (selectedIndex > this.packs.length - this.visiblePacksCount) {
      newIndex = -selectedIndex + this.visiblePacksCount - 1;
    } else {
      newIndex = -selectedIndex + 1;
    }

    this.scrollIndex = isRtl ? Math.abs(newIndex) : newIndex;
  }

  @action
  onInsertElement(element) {
    this.element = element;
    this.args.params.popupDidOpen?.();
  }

  @action
  onDestroy() {
    this.scrolling.unbindScrollDown(this.getDialogWrapper());
    this.args.params.popupDidClose?.();
    this.dynamicPopups.closeByPopupName('mw-stickers-picker');
  }

  get showMyAndAiStickers() {
    return this.features.isEnabled('web_are_ai_stickers_enabled') && this.args.params.aiStickerEnabled;
  }

  get scrollStyle() {
    return htmlSafe(`transform: translateX(${this.scrollIndex * 48}px)`);
  }

  get selectedStickers() {
    return this.stickers && this.stickers[`stickers-${this.selectedId}`]?.stickers || [];
  }

  @computed('storeState.purchasedItems.length', 'selectedId')
  get isOwned() {
    if (this.packs.find((p) => p.id === this.selectedId)?.isFree) {
      return true;
    }
    const purchased = this.storeState.purchasedItems.filter((el) => ~el.indexOf('stickers-'));
    return purchased.indexOf('stickers-' + this.selectedId) > -1;
  }

  get selectedPackName() {
    const pack = this.packs.find((p) => p.id === this.selectedId);
    return pack ? pack.name : this.selectedId;
  }

  get selectedPackLogoUrl() {
    // Construct the logo URL from the selectedId
    return `/assets/images/icons/stickers/${this.selectedId}.png`;
  }

  get sprite() {
    if (!this.stickers) return null;

    return {
      url: this.stickers[`stickers-${this.selectedId}`].pngSpriteSheet.default,
      size: this.stickers[`stickers-${this.selectedId}`].pngSpriteSize,
    };
  }

  get arrowLeftVisible() {
    if (this.packs.length <= this.visiblePacksCount) return false;

    const isRtl = document.dir;
    return isRtl ? this.packs.length - this.scrollIndex > this.visiblePacksCount : this.scrollIndex < 0;
  }

  get arrowRightVisible() {
    if (this.packs.length <= this.visiblePacksCount) return false;

    const isRtl = document.dir;
    return isRtl ? this.scrollIndex > 0 : this.scrollIndex + this.packs.length > this.visiblePacksCount;
  }

  get isGeneratedStickers() {
    return this.selectedId === 'my_sticker' || this.selectedId === 'gen_sticker';
  }

  get isMyStickers() {
    return this.selectedId === 'my_sticker';
  }

  get generatedStickers() {
    if (isEmpty(this.aiStickers)) {
      return;
    }
    return this.aiStickers.map((sticker) => {
      sticker.url = `${location.origin}/api/v2/stickers/generated/${sticker.id}/file`;
      return sticker;
    });
  }

  get popupSize() {
    return {
      width: 392,
      height: 392,
    };
  }

  getPlacement() {
    const isRtl = document.dir;

    const placement = reposition(this.args.params.parent, this.popupSize),
      rectParent = this.args.params.parent.getBoundingClientRect(),
      rectParentLeft = rectParent.left + document.body.scrollLeft;

    //adjust placement for chat giphy
    if (this.args.params.origin === 'chat' || this.args.params.origin === 'expanded-chat') {
      this.element.style.position = 'fixed';

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

      placement.top = placement.top - scrollTop;
    }

    if (this.args.params.origin === 'chat') {
      if (!isRtl) placement.left = rectParentLeft - this.popupSize.width + 30;
    } else if (this.args.params.origin === 'expanded-chat') {
      if (isRtl) placement.left = rectParentLeft - this.popupSize.width + 30;
      else placement.left = rectParentLeft;
    }

    return placement;
  }

  getDialogWrapper() {
    return document.querySelector('.dialog_wrapper');
  }

  // this function checks if hardcoded packs from the picker are also found in the store items
  mapStoreItems() {
    const mapPacks = () => {
      const storePacks = this.storeState.storeItems.filter((item) => item.id.startsWith('stickers-'));
      this.packs = this.packs.map((pack) => {
        const storeItem = storePacks.find((item) => item.id === `stickers-${pack.id}`);
        return {
          ...pack,
          hidden: !storeItem && !pack.isFree,
        };
      });
      this.storePacksChecked = true;
    };

    if (this.storeState.storeItemsLoaded) {
      mapPacks();
    } else {
      fetchStoreItems().then(() => {
        mapPacks();
      });
    }
  }

  @action
  async loadMyStickers() {
    if (!this.features.isEnabled('web_are_ai_stickers_enabled')) {
      return;
    }

    this.loadGeneratedStickers.perform();
  }

  @dropTask * loadGeneratedStickers() {
    const response = yield StickerApi.loadGeneratedSticker();
    yield response.stickers.forEach(async (sticker) => {
      return await StickerApi.downloadGeneratedSticker(sticker.id);
    });
    this.aiStickers = response.stickers;
  }

  @action
  selectSticker(sticker) {
    if (sticker.status) {
      this.args.params.setSticker({ id: sticker.id, status: 'done' });
    } else {
      if (!this.isOwned) return;
      PurchasesStore.send('handleLastUsedStickerPack', this.selectedId);
      this.args.params.setSticker({ package: 'stickers-' + this.selectedId, id: sticker.name, data: sticker });
    }

    this.close();
  }

  @action
  selectPack(id, event) {
    event.stopPropagation();
    event.preventDefault();
    this.element.querySelector('main').scrollTop = 0;
    this.selectedId = id;
  }

  @action
  scrollLeft(event) {
    event.stopPropagation();
    event.preventDefault();
    this.scrollIndex--;
  }

  @action
  scrollRight(event) {
    event.stopPropagation();
    event.preventDefault();
    this.scrollIndex++;
  }

  @action
  buyPack() {
    this.dynamicPopups.closeAll();
    dispatcher.dispatch('purchase', 'checkout', 'stickers-' + this.selectedId);
  }
}
